import React, { useRef, Fragment } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Table,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import { AvForm, AvField } from "availity-reactstrap-validation";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { toggleRightSidebar } from "../../store/actions";
import { withTranslation } from "react-i18next";
import { connect } from "react-redux";
import { useQuery, useLazyQuery, useMutation } from "react-apollo";
import { GET_DEPOSIT_WORKFLOWS } from "../../graphql/query/GET_DEPOSIT_WORKFLOWS";
import { GET_DEPOSIT_WORKFLOW } from "../../graphql/query/GET_DEPOSIT_WORKFLOW";
import { GET_PAYMENT_TYPES } from "../../graphql/query/GET_PAYMENT_TYPES";
import { GET_BANK_ACCOUNTS } from "../../graphql/query/GET_BANK_ACCOUNTS";
import { CREATE_ONE_DEPOSIT, LINK_TRANSACTION_TO_WORKFLOW, FIND_OR_CREATE_MAIN_CREDIT_WALLET } from "../../graphql/mutation";
import _ from "lodash";
import { Link, useHistory } from "react-router-dom";
import InfiniteScroll from "react-infinite-scroll-component";
import moment from "moment";
import TransactionItem from "../Transactions/transaction-item";
import ToastHelper from "../../helpers/ToastHelper";
import NumberFormat from "react-number-format";
import DropZone from "../Workbench/Components/drop-zone";
import Compressor from "compressorjs";
import CloudinaryHelper from "../../helpers/CloudinaryHelper";

const StatusBadge = ({ workflow }) => {
  if (workflow.completedAt) {
    return <span className="badge badge-success px-3 py-2 font-size-13">Completed</span>;
  } else if (workflow.errorMessage) {
    return <span className="badge badge-danger px-3 py-2 font-size-13">Error</span>;
  } else if (workflow.isActive) {
    return <span className="badge badge-warning px-3 py-2 font-size-13">In Progress</span>;
  } else {
    return <span className="badge badge-secondary px-3 py-2 font-size-13">Inactive</span>;
  }
};

const WorkflowTypeBadge = ({ workflowType }) => {
  // Format workflowType from camelCase to readable format
  const formatType = (type) => {
    if (!type) return '';
    return type
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  };
  
  return (
    <span className="badge badge-primary px-2 py-1 font-size-11 ml-2" style={{ fontWeight: 'normal' }}>
      {formatType(workflowType)}
    </span>
  );
};

const WorkflowItem = ({ workflow: initialWorkflow, onSupersede, refreshWorkflowId }) => {
  const history = useHistory();
  const [countdown, setCountdown] = React.useState(15);
  const [currentWorkflow, setCurrentWorkflow] = React.useState(initialWorkflow);
  const [isExpanded, setIsExpanded] = React.useState(false);
  const isFinished = !!(currentWorkflow.completedAt || currentWorkflow.errorMessage);

  const [fetchWorkflow, { loading: refreshing }] = useLazyQuery(GET_DEPOSIT_WORKFLOW, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      if (data?.workflow) {
        setCurrentWorkflow(data.workflow);
      }
    },
  });

  const handleRefresh = React.useCallback(() => {
    fetchWorkflow({ variables: { id: currentWorkflow.id } });
  }, [fetchWorkflow, currentWorkflow.id]);

  React.useEffect(() => {
    let interval = null;
    if (!isFinished) {
      interval = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            handleRefresh();
            return 15;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      setCountdown(15);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isFinished, handleRefresh]);

  // Watch for external refresh signal
  React.useEffect(() => {
    if (refreshWorkflowId && refreshWorkflowId === currentWorkflow.id) {
      handleRefresh();
    }
  }, [refreshWorkflowId, currentWorkflow.id, handleRefresh]);

  const workflow = currentWorkflow;

  return (
    <Col lg="12">
      <Card className="mb-3" style={{ borderLeft: `4px solid ${workflow.completedAt ? '#34c38f' : workflow.errorMessage ? '#f46a6a' : workflow.isActive ? '#f1b44c' : '#74788d'}` }}>
        <CardBody>
          {/* Header with Serial No and Status */}
          <div className="d-flex justify-content-between align-items-start mb-3">
            <div className="d-flex align-items-start">
              <div className="mr-3">
                <h6 className="mb-1 font-weight-bold d-flex align-items-center">
                  {workflow.serialNo}
                  <WorkflowTypeBadge workflowType={workflow.workflowType} />
                </h6>
                <small className="text-muted">
                  {moment(workflow.createdAt).format("YYYY-MM-DD HH:mm:ss")}
                </small>
              </div>
              {!isFinished && (
                <div className="d-flex align-items-center">
                  <span className="badge badge-soft-info font-size-11 px-2 py-1 mr-2" style={{ borderRadius: "5px" }}>
                    <i className="bx bx-sync bx-spin mr-1"></i>
                    {countdown}s
                  </span>
                  <i 
                    className={`bx bx-refresh font-size-18 text-primary cursor-pointer ${refreshing ? 'bx-spin' : ''}`}
                    onClick={() => {
                      handleRefresh();
                      setCountdown(15);
                    }}
                    title="Refresh Now"
                  ></i>
                </div>
              )}
            </div>
            <div className="d-flex align-items-center flex-wrap">
              {!workflow.completedAt && (
                <span className="badge-info px-3 py-2 font-size-12 mr-2" style={{ borderRadius: "5px" }}>
                  Step {workflow.currentStep}
                </span>
              )}
              <StatusBadge workflow={workflow} />
            </div>
          </div>

          {/* Player Info */}
          <div className="mb-3 p-2 bg-light rounded">
            <div className="d-flex align-items-center">
              <i className="bx bx-user-circle font-size-26 mr-2 text-primary"></i>
              <div>
                <div className="font-weight-medium d-flex align-items-center">
                  {workflow.player?.username}
                  <div 
                    className="cursor-pointer ml-2" 
                    onClick={() => {
                      history.push({
                        pathname: "/cashier-workbench",
                        target: "_blank",
                        state: {
                          user: {
                            node: workflow.player,
                          },
                        },
                      });
                    }}
                    title="Open in Workbench"
                  >
                    <i className="bx bxs-wrench font-size-18 text-primary"></i>
                  </div>
                </div>
                <small className="text-muted">{workflow.player?.name}</small>
              </div>
            </div>
          </div>

          {/* Info Grid - Amount, Payment Type, Company Bank, Current Step */}
          {!workflow.completedAt && workflow.currentStepTransaction && (
            <div className="mb-3 p-2 bg-soft-info rounded">
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  <i className="bx bx-loader-circle font-size-20 mr-2 text-info"></i>
                  <div>
                    <div className="font-size-12 text-muted">Current Step Transaction</div>
                    <div className="font-weight-medium">
                      {workflow.currentStepTransaction.transactionType}
                    </div>
                  </div>
                </div>
                <span className={`badge px-2 py-1 font-size-12 ${
                  workflow.currentStepTransaction.transactionStatus === 'Completed' || workflow.currentStepTransaction.kioskStatus === 'Completed' 
                    ? 'badge-soft-success' 
                    : workflow.currentStepTransaction.transactionStatus === 'Rejected' || workflow.currentStepTransaction.kioskStatus === 'Failed'
                      ? 'badge-soft-danger'
                      : 'badge-soft-warning'
                }`}>
                  {workflow.currentStepTransaction.kioskStatus || workflow.currentStepTransaction.transactionStatus}
                </span>
              </div>
            </div>
          )}
          <div className="row mb-3">
            <div className="col-6">
              <div className="d-flex">
                <span className="text-muted mr-2">Amount:</span>
                <span className="font-weight-bold text-success">${parseFloat(workflow.amount || 0).toFixed(2)}</span>
              </div>
            </div>
            <div className="col-6">
              <div className="d-flex">
                <span className="text-muted mr-2">Company Bank:</span>
                <span>{workflow.companyBankAccount ? `${workflow.companyBankAccount?.bank?.name} - ${workflow.companyBankAccount?.accountName} / ${workflow.companyBankAccount?.accountNo}` : "-"}</span>
              </div>
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-6">
              <div className="d-flex">
                <span className="text-muted mr-2">Payment Type:</span>
                <span>{workflow.paymentType?.name || "-"}</span>
              </div>
            </div>
            <div className="col-6">
              <div className="d-flex">
                <span className="text-muted mr-2">To Game Account:</span>
                <span>{`${workflow.toGameAccount?.kioskAccount?.platform?.name} [${workflow.toGameAccount?.username}]` || "-"}</span>
              </div>
            </div>
          </div>
         <div className="row mb-3">
            {workflow.completedAt && (
              <div className="col-6">
                <div className="d-flex">
                  <span className="text-muted mr-2">Completed At:</span>
                  <span>{moment(workflow.completedAt).format("YYYY-MM-DD HH:mm:ss")}</span>
                </div>
              </div>
            )}
          </div>

          {/* Error Message */}
          {workflow.errorMessage && (
            <div className="alert alert-danger py-2 mb-3" role="alert">
              <small><i className="bx bx-error-circle mr-1"></i>{workflow.errorMessage}</small>
            </div>
          )}

          {/* Transactions Section */}
          {workflow.transactions?.edges?.length > 0 && (
            <div className="mt-3 pt-3 border-top transactions-page">
              <h6 
                className="mb-2 font-size-14 cursor-pointer d-flex align-items-center justify-content-between" 
                onClick={() => setIsExpanded(!isExpanded)}
                style={{ cursor: 'pointer' }}
              >
                <span>
                  <i className="bx bx-transfer mr-1"></i>
                  Transactions ({workflow.transactions.edges.length})
                </span>
                <i className={`bx ${isExpanded ? 'bx-chevron-up' : 'bx-chevron-down'} font-size-18`}></i>
              </h6>
              {isExpanded && (
                <div className="table-responsive" style={workflow.transactions?.edges?.length === 1 ? {overflow: "visible"} : {overflow: "scroll"}}>
                  <Table className="table mb-0">
                    <thead>
                      <tr className="font-size-12">
                        <th>Serial No</th>
                        <th>Created At</th>
                        <th>Type</th>
                        <th>Username</th>
                        <th>Name</th>
                        <th>Image</th>
                        <th>Amount</th>
                        <th>Movement</th>
                        <th>Kiosk Remark</th>
                        <th>Updated At</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody className="table-body">
                      {_.sortBy(workflow.transactions.edges, ['node.stepNumber']).map((transaction, txnIndex) => (
                        <TransactionItem
                          key={txnIndex}
                          refetch={handleRefresh}
                          transaction={transaction}
                          onDone={handleRefresh}
                          isFromWorkflow
                          allowSupersede={transaction.node.transactionType === "Deposit" && (transaction.node.transactionStatus !== "Completed" && transaction.node.transactionStatus !== "Pending") && _.isEmpty(transaction.node.supersededById)}
                          onSupersede={() => onSupersede && onSupersede(workflow)}
                          onManageInWorkbench={() => {
                            history.push({
                              pathname: "/cashier-workbench",
                              target: "_blank",
                              state: {
                                user: {
                                  node: transaction.node.player,
                                },
                              },
                            });
                          }}
                        />
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </div>
          )}

          {/* Updated At Footer */}
          <div className="mt-3 pt-2 border-top text-muted font-size-12">
            <i className="bx bx-time-five mr-1"></i>
            Updated: {moment(workflow.updatedAt).format("YYYY-MM-DD HH:mm:ss")}
          </div>
        </CardBody>
      </Card>
    </Col>
  );
};

function WorkflowDeposit(props) {
  const { t } = props;
  const first = 50;

  const [search, setSearch] = React.useState("");
  const [searchValue, setSearchValue] = React.useState("");
  const [isSearch, setIsSearch] = React.useState(false);
  const [afterCursor, setAfterCursor] = React.useState(null);
  const [pageInfo, setPageInfo] = React.useState(false);
  const [isFetchMore, setIsFetchMore] = React.useState(false);
  const [orderBy, setOrderBy] = React.useState("createdAt");
  const [orderDirection, setOrderDirection] = React.useState("DESC");
  
  // Modal state
  const [isCreateModal, setIsCreateModal] = React.useState(false);
  const [supersedeWorkflow, setSupersedeWorkflow] = React.useState(null);
  
  // Form state
  const [selectedPlayer, setSelectedPlayer] = React.useState(null);
  const [selectedPaymentType, setSelectedPaymentType] = React.useState("");
  const [amount, setAmount] = React.useState("");
  const [selectedPlayerBankAccount, setSelectedPlayerBankAccount] = React.useState("");
  const [selectedCompanyBankAccount, setSelectedCompanyBankAccount] = React.useState("");
  const [selectedFile, setSelectedFile] = React.useState(null);
  
  // Refresh signal for specific workflow
  const [refreshWorkflowId, setRefreshWorkflowId] = React.useState(null);

  const baseFilter = {
    workflowType: { eq: "DirectDepositToGame" },
  };

  const Filter = () => {
    if (_.isEmpty(search)) {
      return { ...baseFilter };
    } else {
      return {
        ...baseFilter,
        or: [
          { serialNo: { iLike: `%${search}%` } },
          { player: { username: { iLike: `%${search}%` } } },
        ],
      };
    }
  };

  const {
    data: workflowData,
    fetchMore,
    loading,
    refetch,
  } = useQuery(GET_DEPOSIT_WORKFLOWS, {
    variables: {
      paging: { first: first },
      filter: Filter(),
      sorting: [{ field: orderBy, direction: orderDirection }],
    },
    fetchPolicy: "network-only",
  });

  const { data: paymentTypesData } = useQuery(GET_PAYMENT_TYPES);

  // Fetch player bank accounts for selected player
  const { data: playerBankAccountsData } = useQuery(GET_BANK_ACCOUNTS, {
    variables: {
      paging: { first: 50 },
      filter: {
        playerId: { eq: selectedPlayer?.id },
        isActive: { is: true },
      },
    },
    skip: !selectedPlayer,
    fetchPolicy: "network-only",
  });

  // Fetch company bank accounts that can deposit
  const { data: companyBankAccountsData } = useQuery(GET_BANK_ACCOUNTS, {
    variables: {
      paging: { first: 50 },
      filter: {
        accountHolderType: { eq: "Company" },
        isActive: { is: true },
        canDeposit: { is: true },
      },
    },
    fetchPolicy: "network-only",
  });

  const [createDeposit, { loading: createLoading }] = useMutation(CREATE_ONE_DEPOSIT);
  const [linkTransactionToWorkflow] = useMutation(LINK_TRANSACTION_TO_WORKFLOW);
  const [getMainCreditWallet, { data: mainCreditWalletData }] = useMutation(FIND_OR_CREATE_MAIN_CREDIT_WALLET);

  const handleSupersede = (workflow) => {
    setSupersedeWorkflow(workflow);
    if (workflow.player) {
      setSelectedPlayer(workflow.player);
      // Get main credit wallet for the player
      getMainCreditWallet({
        variables: {
          userId: workflow.player.id
        }
      });
    }
    if (workflow.amount) {
      setAmount(workflow.amount.toString());
    }
    // Set payment type to Local Bank Transfer
    const localBankTransfer = paymentTypesData?.paymentTypes?.edges?.find(
      (pt) => pt.node.name === "Local Bank Transfer"
    );
    if (localBankTransfer) {
      setSelectedPaymentType(localBankTransfer.node.id);
    }
    setIsCreateModal(true);
  };

  const resetModal = () => {
    setIsCreateModal(false);
    setSupersedeWorkflow(null);
    setSelectedPlayer(null);
    setSelectedPaymentType("");
    setAmount("");
    setSelectedPlayerBankAccount("");
    setSelectedCompanyBankAccount("");
    setSelectedFile(null);
  };

  // Compress image before upload
  const Compress = (file) => {
    const fileType = file["type"];
    const validImageTypes = ["image/jpeg", "image/png"];
    if (!validImageTypes.includes(fileType)) {
      return file;
    }

    return new Promise((resolve, reject) => {
      new Compressor(file, {
        quality: 1,
        success: resolve,
        error: reject,
      });
    });
  };

  const handleCreateDeposit = async (event, values) => {
    try {
      let receiptUrl = null;

      // Upload receipt if selected
      if (selectedFile) {
        const imageData = await Compress(selectedFile);
        const result = await CloudinaryHelper.upload(
          imageData,
          `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUD_NAME}/image/upload?upload_preset=rjhrtxfi`
        );
        receiptUrl = result.data.url;
      }

      const input = {
        amount: parseFloat(amount.toString().replace(/,/g, "")),
        playerId: selectedPlayer.id,
        paymentTypeId: selectedPaymentType,
        ...(selectedPlayerBankAccount && { playerBankAccountId: selectedPlayerBankAccount }),
        ...(selectedCompanyBankAccount && { companyBankAccountId: selectedCompanyBankAccount }),
        toWalletId: mainCreditWalletData?.findOrCreateMainCreditWallet?.id,
        ...(receiptUrl && { receiptUrl }),
        bonusAmount: 0,
        requiredRolloverAmount: parseFloat(amount.toString().replace(/,/g, "")),
      };

      const depositResult = await createDeposit({
        variables: {
          input: {
            deposit: { ...input },
          },
        },
      });

      // Link the new transaction to the superseded workflow
      if (supersedeWorkflow && depositResult?.data?.createOneDeposit?.id) {
        await linkTransactionToWorkflow({
          variables: {
            input: {
              transactionId: depositResult.data.createOneDeposit.id,
              workflowId: supersedeWorkflow.id,
              stepNumber: 1, // Deposit step in workflow
            },
          },
        });
      }

      ToastHelper.toggleToast({
        message: "Supersede deposit created successfully!",
        title: "",
        onClick: function () {},
      });

      // Trigger refresh on the specific workflow that was superseded
      if (supersedeWorkflow) {
        setRefreshWorkflowId(supersedeWorkflow.id);
        // Clear the refresh signal after a short delay
        setTimeout(() => setRefreshWorkflowId(null), 100);
      }

      resetModal();
    } catch (e) {
      ToastHelper.toggleToast({
        toastType: "error",
        message: e.message || e,
        title: "",
        onClick: function () {},
      });
    }
  };

  React.useEffect(() => {
    if (workflowData) {
      setAfterCursor(workflowData.workflows.pageInfo.endCursor);
      setPageInfo(workflowData.workflows.pageInfo);
    }
  }, [workflowData]);

  React.useEffect(() => {
    if (isFetchMore) {
      fetchMore({
        variables: {
          paging: { first: first, after: afterCursor },
          filter: Filter(),
          sorting: [{ field: orderBy, direction: orderDirection }],
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          const newEdges = fetchMoreResult.workflows.edges;
          const pageInfo = fetchMoreResult.workflows.pageInfo;
          setIsFetchMore(false);
          setAfterCursor(pageInfo.endCursor);

          return newEdges.length
            ? {
                workflows: {
                  __typename: previousResult.workflows.__typename,
                  edges: _.uniqBy(
                    [...previousResult.workflows.edges, ...newEdges],
                    "node.id"
                  ),
                  pageInfo,
                },
              }
            : previousResult;
        },
      });
    }
  }, [isFetchMore]); // eslint-disable-line react-hooks/exhaustive-deps

  const debouncedSave = useRef(
    _.debounce((nextValue) => setSearch(nextValue), 300)
  ).current;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Workflow" breadcrumbItem="Deposit" />
          <div className="search-bar-container">
            <div className="button-add-bank-container">
              <div className="app-search d-none d-lg-block">
                <div className="position-relative">
                  <input
                    value={searchValue}
                    type="text"
                    className="form-control"
                    placeholder="Search..."
                    onChange={(e) => {
                      setSearchValue(e.target.value);
                      debouncedSave(e.target.value);
                    }}
                  />
                  <span className="bx bx-search-alt"></span>
                </div>
              </div>
              <div className="d-flex align-items-center">
                <div className="dropdown d-inline-block d-lg-none ml-2">
                  <button
                    type="button"
                    className="btn header-item noti-icon waves-effect"
                    onClick={() => setIsSearch(!isSearch)}
                  >
                    <i className="mdi mdi-magnify"></i>
                  </button>
                  <div
                    className={
                      isSearch
                        ? "dropdown-menu dropdown-menu-lg dropdown-menu-right p-0 show"
                        : "dropdown-menu dropdown-menu-lg dropdown-menu-right p-0"
                    }
                  >
                    <form className="p-3">
                      <div className="form-group m-0">
                        <div className="input-group">
                          <input
                            value={searchValue}
                            type="text"
                            className="form-control"
                            placeholder="Search..."
                            onChange={(e) => {
                              setSearchValue(e.target.value);
                              debouncedSave(e.target.value);
                            }}
                          />
                          <div className="input-group-append">
                            <button className="btn btn-primary" type="submit">
                              <i className="mdi mdi-magnify"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {workflowData && (
            <InfiniteScroll
              dataLength={workflowData.workflows.edges.length}
              next={() => setIsFetchMore(true)}
              hasMore={pageInfo.hasNextPage}
              loader={
                <div className="text-center my-3">
                  <Link to="#" className="text-success">
                    <i className="bx bx-hourglass bx-spin mr-2"></i> Load more
                  </Link>
                </div>
              }
              endMessage={
                <div className="text-center my-3">
                  <div>Yay! You have seen it all</div>
                </div>
              }
            >
              <Row>
                {workflowData.workflows.edges.map((workflow, index) => (
                  <WorkflowItem 
                    key={workflow.node.id} 
                    workflow={workflow.node} 
                    onSupersede={handleSupersede}
                    refreshWorkflowId={refreshWorkflowId}
                  />
                ))}
              </Row>
            </InfiniteScroll>
          )}
          <div className="load-more-container">
            {loading && (
              <Col xs="12">
                <div className="text-center my-3">
                  <Link to="#" className="text-success">
                    <i className="bx bx-hourglass bx-spin mr-2"></i> Load more
                  </Link>
                </div>
              </Col>
            )}
          </div>
        </Container>
      </div>

      {/* Supersede Deposit Modal */}
      <Modal
        isOpen={isCreateModal}
        size="lg"
        centered={true}
        tabIndex="-1"
      >
        <div className="modal-content new-account-page">
          <ModalHeader toggle={resetModal}>
            {t("Supersede Deposit")}
          </ModalHeader>
          <ModalBody>
            <div className="twitter-bs-wizard">
              <AvForm
                className="needs-validation"
                onValidSubmit={handleCreateDeposit}
              >
                <Row>
                  <Col lg="12">
                    <FormGroup>
                      <Label>{t("Player")}</Label>
                      <Input
                        type="text"
                        value={selectedPlayer ? `${selectedPlayer.serialNo} / ${selectedPlayer.username}` : ""}
                        disabled
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col lg="12">
                    <FormGroup>
                      <Label>{t("Amount")} *</Label>
                      <NumberFormat
                        className="form-control"
                        value={amount}
                        thousandSeparator
                        decimalScale={2}
                        fixedDecimalScale
                        placeholder="Enter Amount"
                        onValueChange={(values) => {
                          setAmount(values.value);
                        }}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col lg="12">
                    <FormGroup>
                      <Label>{t("Payment Type")} *</Label>
                      <Input
                        type="text"
                        value="Local Bank Transfer"
                        disabled
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col lg="12">
                    <FormGroup>
                      <Label>{t("Player Bank Account")}</Label>
                      <select
                        className="custom-select"
                        value={selectedPlayerBankAccount}
                        onChange={(e) => setSelectedPlayerBankAccount(e.target.value)}
                      >
                        <option value="">Select Player Bank Account</option>
                        {playerBankAccountsData?.bankAccounts?.edges?.map((ba) => (
                          <option key={ba.node.id} value={ba.node.id}>
                            {ba.node.bank?.name} - {ba.node.accountName} / {ba.node.accountNo}
                          </option>
                        ))}
                      </select>
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col lg="12">
                    <FormGroup>
                      <Label>{t("Company Bank Account")}</Label>
                      <select
                        className="custom-select"
                        value={selectedCompanyBankAccount}
                        onChange={(e) => setSelectedCompanyBankAccount(e.target.value)}
                      >
                        <option value="">Select Company Bank Account</option>
                        {companyBankAccountsData?.bankAccounts?.edges?.map((ba) => (
                          <option key={ba.node.id} value={ba.node.id}>
                            {ba.node.bank?.name} - {ba.node.accountName} / {ba.node.accountNo}
                          </option>
                        ))}
                      </select>
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col lg="12">
                    <FormGroup>
                      <Label>{t("Receipt (Optional)")}</Label>
                      <DropZone
                        selectedFile={selectedFile}
                        onFileDrop={(file) => setSelectedFile(file)}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row className="mt-3">
                  <Col lg="12" className="text-right">
                    <Button
                      color="secondary"
                      className="mr-2"
                      onClick={resetModal}
                      type="button"
                    >
                      {t("Cancel")}
                    </Button>
                    <Button
                      color="primary"
                      type="submit"
                      disabled={createLoading || !amount || !selectedPaymentType}
                    >
                      {createLoading && (
                        <i className="fas fa-spinner fa-spin mr-2"></i>
                      )}
                      {t("Create Supersede Deposit")}
                    </Button>
                  </Col>
                </Row>
              </AvForm>
            </div>
          </ModalBody>
        </div>
      </Modal>
    </React.Fragment>
  );
}

const mapStatetoProps = (state) => {
  const { layoutType } = state.Layout;
  return { layoutType };
};

export default connect(mapStatetoProps, { toggleRightSidebar })(
  withTranslation()(WorkflowDeposit)
);
